import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  addEmailDomain,
  checkEmailDomain,
  emailDomainFixQuery,
  emailDomainsQuery,
  fixEmailDomain,
  removeEmailDomain,
  type EmailDomain,
  type EmailDomainResult,
  type FixChoice,
} from '../../api/securiteeEmailDomains'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Content } from '../../ui/Shell'
import { Spinner } from '../../ui/Spinner'
import './email-domains.css'

const SPF: Record<EmailDomainResult['spf']['status'], string> = {
  strict: '-all', soft: '~all', neutral: '?all', open: '+all', no_all: 'no "all"', missing: 'missing', multiple: '2+ records', redirect: 'redirect',
}
const DKIM: Record<EmailDomainResult['dkim']['status'], string> = { ok: 'key found', m365_off: 'off in Microsoft 365', missing: 'not found', 'n/a': '—' }

function dmarcText(r: EmailDomainResult): string {
  if (r.dmarc.status === 'missing') return 'missing'
  if (r.dmarc.status === 'multiple') return '2+ records'
  return r.dmarc.policy ? `p=${r.dmarc.policy}` : 'no policy'
}

/** Verde / portocaliu dupa constatarea partii aleia ("parked" vine mereu cu constatarea SPF / DMARC care lipseste). */
const good = (r: EmailDomainResult, part: 'spf' | 'dmarc' | 'dkim') => !r.findings.some((f) => f.key === part)

/**
 * Securitee > Email domains: domeniile de mail ale firmei si DNS-ul lor public
 * (audit R40 DKIM, R41 domenii de brand). FIX: configuratia propusa; tichet
 * catre grupul DNS cand DNS-ul e la noi, altfel inregistrarile de dat mai departe.
 */
export function EmailDomainsPage() {
  const client = useQueryClient()
  const page = useQuery({ ...emailDomainsQuery(), placeholderData: keepPreviousData })
  const [name, setName] = useState('')
  const [addError, setAddError] = useState('')
  const [busy, setBusy] = useState(false)
  const [fixing, setFixing] = useState<EmailDomain | null>(null)
  const data = page.data

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <Content>
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
        </div>
      </Content>
    )
  }
  if (!data) return <Spinner />

  const put = (next: unknown) => client.setQueryData(emailDomainsQuery().queryKey, next as typeof data)
  const add = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setAddError('')
    try {
      put(await addEmailDomain(name))
      setName('')
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'The domain could not be added.')
    } finally {
      setBusy(false)
    }
  }
  const act = async (fn: () => Promise<unknown>) => {
    try {
      await fn()
    } catch (err) {
      toast(err instanceof Error ? err.message : 'That did not work.', 'Email domains', 'error')
    }
  }

  return (
    <Content>
      <div id="content" className="ed-page">
        <header className="panel-heading">
          <h3>
            <strong>Email domains</strong>
          </h3>
          <label className="color">SPF, DMARC and DKIM read from public DNS, checked again every day · read only</label>
        </header>

        {data.can_edit && (
          <form className="ed-add" onSubmit={add}>
            <input className="form-control input-sm" aria-label="Email domain" placeholder="anagrup.ro" value={name} onChange={(e) => setName(e.target.value)} />
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy || name.trim() === ''}>
              Add domain
            </button>
            {addError && <span className="ed-add-error text-danger">{addError}</span>}
          </form>
        )}

        {data.domains.length === 0 ? (
          <p className="ed-empty">
            No email domains yet.{' '}
            {data.can_edit ? 'Add the company mail domains, including brand domains that do not send mail.' : 'Someone with the XDR settings right can add them.'}
          </p>
        ) : (
          <div className="ed-table-wrap">
            <table className="table ed-table">
              <thead>
                <tr>
                  <th scope="col">Domain</th>
                  <th scope="col">DNS</th>
                  <th scope="col">MX</th>
                  <th scope="col">SPF</th>
                  <th scope="col">DMARC</th>
                  <th scope="col">DKIM</th>
                  <th scope="col">Findings</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.domains.map((d) => {
                  const r = d.result
                  const cell = (ok: boolean, text: string) => <span className={ok ? 'ed-ok' : 'ed-bad'}>{text}</span>
                  return (
                    <tr key={d.id} id={`ed-row-${d.id}`}>
                      <td>
                        <strong>{d.domain}</strong> <span className="ed-when">{d.checked_at ? `checked ${d.checked_at.slice(0, 16)} UTC` : 'not checked yet'}</span>
                        {d.error && <div className="ed-error">{d.error}</div>}
                      </td>
                      <td>{r ? (r.managed ? 'Class IT' : (r.ns[0] ?? '—')) : '—'}</td>
                      <td>{r ? (r.parked ? 'none (parked)' : r.microsoft365 ? 'Microsoft 365' : (r.mx[0] ?? '—')) : '—'}</td>
                      <td>{r ? cell(good(r, 'spf'), SPF[r.spf.status]) : '—'}</td>
                      <td>{r ? cell(good(r, 'dmarc'), dmarcText(r)) : '—'}</td>
                      <td>{r ? (r.dkim.status === 'n/a' ? DKIM['n/a'] : cell(good(r, 'dkim'), DKIM[r.dkim.status])) : '—'}</td>
                      <td>
                        {r && r.findings.length === 0 && <span className="ed-ok">OK</span>}
                        {r?.findings.map((f) => (
                          <span key={f.key + f.text} className="label label-warning ed-finding">
                            {f.text}
                          </span>
                        ))}
                      </td>
                      <td className="ed-actions">
                        {r && r.findings.length > 0 && (
                          <button type="button" className={r.managed ? 'btn btn-warning btn-xs' : 'btn btn-default btn-xs'} onClick={() => setFixing(d)}>
                            {r.managed ? 'FIX' : 'Records'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-default btn-xs"
                          onClick={() =>
                            act(async () => {
                              const next = await checkEmailDomain(d.id)
                              put({ ...data, domains: data.domains.map((x) => (x.id === d.id ? next : x)) })
                            })
                          }
                        >
                          Check now
                        </button>
                        {data.can_edit && (
                          <button type="button" className="btn btn-link btn-xs ed-remove" aria-label={`Remove ${d.domain}`} onClick={() => act(async () => put(await removeEmailDomain(d.id)))}>
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {fixing?.result && <FixDialog domain={fixing} result={fixing.result} canTicket={data.can_ticket} onClose={() => setFixing(null)} />}
      </div>
    </Content>
  )
}

/** Configuratia propusa: alegerile o recalculeaza pe server; tichet sau de copiat. */
function FixDialog({ domain, result, canTicket, onClose }: { domain: EmailDomain; result: EmailDomainResult; canTicket: boolean; onClose(): void }) {
  const [choice, setChoice] = useState<FixChoice>({
    all: '-all',
    policy: result.parked || result.dmarc.policy === 'reject' ? 'reject' : 'quarantine',
    rua: '',
  })
  const [sending, setSending] = useState(false)
  const fix = useQuery({ ...emailDomainFixQuery(domain.id, choice), placeholderData: keepPreviousData })
  const records = fix.data ? `TXT  ${domain.domain}  "${fix.data.spf}"\nTXT  _dmarc.${domain.domain}  "${fix.data.dmarc}"` : ''
  const ticket = result.managed && canTicket

  const open = async () => {
    setSending(true)
    try {
      const r = await fixEmailDomain(domain.id, choice)
      toast(`Ticket #${r.ticket_id} opened for the DNS team.`, 'Email domains', 'success')
      onClose()
    } catch (err) {
      toast(err instanceof Error ? err.message : 'The ticket could not be opened.', 'Email domains', 'error')
    } finally {
      setSending(false)
    }
  }
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(records)
      toast('Records copied.', 'Email domains', 'success')
    } catch {
      toast('Select the records and copy them.', 'Email domains', 'error')
    }
  }

  return (
    <Modal
      id="email-domain-fix"
      open
      onClose={onClose}
      title={`${ticket ? 'FIX' : 'Records for'} ${domain.domain}`}
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          {ticket ? (
            <button type="button" className="btn btn-primary" disabled={sending || !fix.data} onClick={open}>
              Open ticket for the DNS team
            </button>
          ) : (
            <button type="button" className="btn btn-primary" disabled={!fix.data} onClick={copy}>
              Copy records
            </button>
          )}
        </>
      }
    >
      <div className="ed-fix">
        {!result.managed && <p className="ed-fix-note">The DNS of {domain.domain} is not hosted by us: send these records to whoever manages it.</p>}
        {result.managed && !canTicket && <p className="ed-fix-note">You cannot open tickets: copy the records instead.</p>}
        <div className="ed-fix-choices">
          {!result.parked && (
            <label>
              SPF ends with
              <select className="form-control input-sm" aria-label="SPF ends with" value={choice.all} onChange={(e) => setChoice({ ...choice, all: e.target.value as FixChoice['all'] })}>
                <option value="-all">-all (refuse other servers)</option>
                <option value="~all">~all (mark them as suspect)</option>
              </select>
            </label>
          )}
          <label>
            DMARC policy
            <select className="form-control input-sm" aria-label="DMARC policy" value={choice.policy} onChange={(e) => setChoice({ ...choice, policy: e.target.value as FixChoice['policy'] })}>
              <option value="none">none (only monitor)</option>
              <option value="quarantine">quarantine (to spam)</option>
              <option value="reject">reject</option>
            </select>
          </label>
          <label>
            DMARC reports to (optional)
            <input className="form-control input-sm" aria-label="DMARC reports to" placeholder="dmarc@example.com" value={choice.rua} onChange={(e) => setChoice({ ...choice, rua: e.target.value })} />
          </label>
        </div>
        <pre className="ed-records">{records}</pre>
        {fix.data && fix.data.dkim.length > 0 && (
          <div className="ed-dkim">
            <strong>DKIM</strong>
            <ul>
              {fix.data.dkim.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  )
}
